import { isPast } from 'date-fns';
import { ReactNode } from 'react';
import { FormattedMessage, defineMessages } from 'react-intl';

import { GenericBanner, Icon } from '../GenericBanner';
import cardSwapIcon from './assets/card_swap_icon.svg';

const messages = defineMessages({
  timeLeft: {
    id: 'CardSwap.TimeUntilNext.timeLeft',
    defaultMessage: 'Free swap in {timeLeft}',
  },
});

export function getCta(
  isFreeTradeAvailable: boolean,
  tradeInProgress: boolean,
  hasExtraSwap: boolean,
  canBuyExtraSwap: boolean
): ReactNode {
  if (tradeInProgress) {
    return (
      <FormattedMessage
        id="CardSwap.Banner.Cta.ContinueSwap"
        defaultMessage="Continue swap"
      />
    );
  }
  if (isFreeTradeAvailable) {
    return (
      <FormattedMessage
        id="CardSwap.Banner.Cta.FreeSwap"
        defaultMessage="Use your free swap"
      />
    );
  }
  if (hasExtraSwap) {
    return (
      <FormattedMessage
        id="CardSwap.Banner.Cta.ExtraSwap"
        defaultMessage="Use your extra swap"
      />
    );
  }
  if (canBuyExtraSwap) {
    return (
      <FormattedMessage
        id="CardSwap.Banner.Cta.GetExtraSwap"
        defaultMessage="Get extra swap"
      />
    );
  }

  return null;
}

function getSubtitle(
  isFreeTradeAvailable: boolean,
  tradeInProgress: boolean,
  hasExtraSwap: boolean,
  canBuyExtraSwap: boolean
): ReactNode {
  if (!tradeInProgress && !isFreeTradeAvailable) {
    if (hasExtraSwap) {
      return (
        <FormattedMessage
          id="CardSwap.Banner.explanation.extraSwap"
          defaultMessage="Improve your roster now and bypass the standard 24-hour window!"
        />
      );
    }
    if (canBuyExtraSwap) {
      return (
        <FormattedMessage
          id="CardSwap.Banner.explanation.getExtraSwap"
          defaultMessage="Get a swap now—exchange cards and bypass the standard 24-hour window."
        />
      );
    }
  }

  return (
    <FormattedMessage
      id="CardSwap.Banner.explanation.freeSwap"
      defaultMessage="Improve your team! Trade in one Common Card for a new one"
    />
  );
}

export type Props = {
  tradeInProgress: boolean;
  nextTradeAvailable: Date;
  onClick: () => void;
  icon?: ReactNode;
  className?: string;
  hasExtraSwap?: boolean;
  canBuyExtraSwap?: boolean;
};

export const CardSwapBanner = ({
  tradeInProgress,
  nextTradeAvailable,
  icon,
  className,
  onClick,
  hasExtraSwap = false,
  canBuyExtraSwap = false,
}: Props) => {
  const isFreeTradeAvailable = isPast(nextTradeAvailable);
  const ctaAvailable =
    tradeInProgress || isFreeTradeAvailable || hasExtraSwap || canBuyExtraSwap;
  const cta = getCta(
    isFreeTradeAvailable,
    tradeInProgress,
    hasExtraSwap,
    canBuyExtraSwap
  );
  const subtitle = getSubtitle(
    isFreeTradeAvailable,
    tradeInProgress,
    hasExtraSwap,
    canBuyExtraSwap
  );

  return (
    <GenericBanner
      title={
        <FormattedMessage
          id="CardSwap.Banner.title"
          defaultMessage="Card swap"
        />
      }
      subtitle={subtitle}
      cta={cta}
      timeLeftMessage={messages.timeLeft}
      ctaAvailable={ctaAvailable}
      nextAvailableDate={nextTradeAvailable}
      icon={icon || <Icon src={cardSwapIcon} alt="" />}
      onClick={onClick}
      className={className}
      showTimeUntilNext={!isFreeTradeAvailable}
    />
  );
};
